// Everything about one material, opened by clicking it anywhere in Inventory.

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, updateRow, useRows, type Row } from '../../lib/api';
import { longDate, qty, shortDate } from '../../lib/format';
import { addKanbanCard, correctCount, localDay, useInventory, useVendors } from '../../lib/inventory';
import { useSession } from '../../lib/session';
import { Btn, Edit, Field, Info, Ln, Loading, Modal, Panel, Tag } from '../../ui/kit';
import { uploadFile, useSignedUrl } from '../job/Files';
import { printKanban } from './KanbanCard';
import { cap, useInventoryNames, useOrderStateNames } from './names';

const number = (t: string) => (t.trim() === '' ? '' : /^-?\d*\.?\d+$/.test(t.trim()) ? t.trim() : undefined);

export function MaterialPanel({ materialId, onClose }: { materialId: string; onClose: () => void }) {
  const s = useSession();
  const names = useInventoryNames();
  const vendors = useVendors();
  const m = useRows('material', { eq: { id: materialId } }).rows[0];
  const canEdit = s.canEdit('screen.purchasing');
  if (!m) {
    return (
      <Modal title="Material" onClose={onClose}>
        <Loading />
      </Modal>
    );
  }
  const save = (patch: Row) => updateRow('material', m.id, patch);
  const bin = m.tracking_mode === 'bin';
  const vendor = vendors.byId.get(m.vendor_id);

  return (
    <Modal
      wide
      onClose={onClose}
      title={
        <>
          {m.description} {m.verified ? <Tag kind="on">verified</Tag> : <Tag kind="un">unverified</Tag>}
        </>
      }
    >
      <div className="row">
        <div className="col stack">
          <Panel label="Stock">
            <div className="form">
              <Field
                label={
                  <>
                    {bin ? 'On hand (not counted: bin)' : 'On hand'}
                    <Info>The count moves only when something is received, used on a card, or corrected. It cannot be typed over: use "Correct the count" below, which asks for the reason and records the change.</Info>
                  </>
                }
              >
                <input type="text" disabled value={bin ? '' : `${qty(m.stock_on_hand)} ${m.unit ?? ''}`} />
              </Field>
              <Field
                label={
                  <>
                    {cap(names.safety_stock)}
                    <Info>Never let stock on hand plus what is already on order fall below this. Falling below it raises a reorder line. Leave it blank on something you do not care to track.</Info>
                  </>
                }
              >
                <Edit disabled={!canEdit} value={m.safety_stock === null ? '' : qty(m.safety_stock)} placeholder="not tracked" parse={number} onSave={(v) => save({ safety_stock: v === '' ? null : Number(v) })} />
              </Field>
              <Field
                label={
                  <>
                    {cap(names.order_quantity)}
                    <Info>What one reorder brings in: what refills the bin. Blank means "order just the shortfall".</Info>
                  </>
                }
              >
                <Edit disabled={!canEdit} value={m.order_quantity === null ? '' : qty(m.order_quantity)} placeholder="the shortfall" parse={(t) => (t.trim() === '' ? '' : /^\d*\.?\d+$/.test(t.trim()) && Number(t) > 0 ? t.trim() : undefined)} onSave={(v) => save({ order_quantity: v === '' ? null : Number(v) })} />
              </Field>
              <Field label="Counted how">
                <select disabled={!canEdit} value={m.tracking_mode} onChange={(e) => void save({ tracking_mode: e.target.value })}>
                  <option value="exact">exact count</option>
                  <option value="bin">by the bin (not counted)</option>
                </select>
              </Field>
              <Field label="Unit">
                <Edit disabled={!canEdit} value={m.unit ?? ''} onSave={(v) => save({ unit: v || null })} />
              </Field>
            </div>
            <OnOrder materialId={m.id} unit={m.unit} />
            {!bin && (canEdit || s.can('screen.receiving')) && <CountCorrection m={m} />}
          </Panel>
          <Movements m={m} />
        </div>
        <div className="col stack">
          <Panel label="Buying">
            <div className="form">
              <Field label="Vendor">
                <select disabled={!canEdit} value={m.vendor_id ?? ''} onChange={(e) => void save({ vendor_id: e.target.value || null, ...(e.target.value && e.target.value === m.alt_vendor_id ? { alt_vendor_id: null } : {}) })}>
                  <option value="">none</option>
                  {vendors.rows.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Alternate vendor">
                <select disabled={!canEdit} value={m.alt_vendor_id ?? ''} onChange={(e) => void save({ alt_vendor_id: e.target.value || null })}>
                  <option value="">none</option>
                  {vendors.rows.filter((v) => v.id !== m.vendor_id).map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label={
                  <>
                    Lead time, days
                    <Info>
                      {vendor ? `${vendor.name}'s default is ${vendor.lead_time_days === null ? 'not set' : `${vendor.lead_time_days} days`}.` : 'No vendor chosen.'} Type a number here only when this material differs. It applies to the primary vendor; a buy from the alternate uses that vendor's default.
                    </Info>
                  </>
                }
              >
                <Edit disabled={!canEdit} value={m.lead_time_days ?? ''} placeholder={vendor?.lead_time_days !== null && vendor?.lead_time_days !== undefined ? `${vendor.lead_time_days} (vendor default)` : 'not known'} parse={(t) => (t.trim() === '' ? '' : /^\d+$/.test(t.trim()) ? t.trim() : undefined)} onSave={(v) => save({ lead_time_days: v === '' ? null : Number(v) })} />
              </Field>
              <Field label="Link on the vendor's site" wide>
                <Edit disabled={!canEdit} value={m.vendor_url ?? ''} placeholder="https://…" onSave={(v) => save({ vendor_url: v || null })} />
              </Field>
              <Field label="Part number">
                <Edit disabled={!canEdit} value={m.part_number ?? ''} onSave={(v) => save({ part_number: v || null })} />
              </Field>
            </div>
            {m.verified && <small>Changing the estimating price, unit or part number returns this material to unverified.</small>}
          </Panel>
          <Kanban m={m} canEdit={canEdit} save={save} />
        </div>
      </div>
      {s.can('see_prices') && <Prices m={m} canEdit={canEdit} save={save} />}
    </Modal>
  );
}

/** The kanban card: what gets printed on it, the cards this material has, and where each one is. */
function Kanban({ m, canEdit, save }: { m: Row; canEdit: boolean; save: (patch: Row) => Promise<unknown> }) {
  const s = useSession();
  const names = useInventoryNames();
  const stateNames = useOrderStateNames();
  const navigate = useNavigate();
  const inv = useInventory();
  const cards = useRows('label', { eq: { kind: 'kanban', target_id: m.id }, order: ['card_no'] });
  const picture = useRows('file', { eq: { id: m.picture_file_id }, enabled: !!m.picture_file_id }).rows[0];
  const url = useSignedUrl(picture?.storage_path);
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const pick = async (list: FileList | null) => {
    const f = list?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const row = await uploadFile(f, 'material', m.id, s.user?.id ?? null);
      await save({ picture_file_id: row.id });
    } finally {
      setBusy(false);
    }
  };
  // where a raised card's request has got to
  const where = (c: Row): string => {
    if (!c.raised_line_id) return 'in its bin';
    const on = inv.orders.find((o) => o.lines.some((l) => l.requests.some((r) => r.id === c.raised_line_id)));
    return `reached ${shortDate(c.raised_at)} · ${on ? `on ${on.po.number} (${stateNames[on.state].toLowerCase()})` : 'waiting to be ordered'}`;
  };
  return (
    <Panel
      label={cap(names.kanban_card)}
      right={
        <Info left>
          The card sits in the bin at the reorder level. Whoever reaches it scans it, which raises the reorder, and puts the card in the reorder slot. Scanning it again when the order is put away sends it back to its bin. One card per bin; a counted item can carry
          one too, as the backstop for when the count has drifted. The card prints 4 × 6 in with everything below on it.
        </Info>
      }
    >
      <div className="form">
        <Field label="Bin location in the shop">
          <Edit disabled={!canEdit} value={m.bin_location ?? ''} placeholder="rack, shelf, bin" onSave={(v) => save({ bin_location: v || null })} />
        </Field>
        <Field label="Picture">
          <span className="row mid" style={{ gap: 8 }}>
            {url && <img src={url} alt="" style={{ height: 44, width: 60, objectFit: 'contain', border: '1px solid var(--line)' }} />}
            <input ref={input} type="file" accept="image/*" className="hide" onChange={(e) => void pick(e.target.files)} />
            {canEdit && (
              <Btn small disabled={busy} onClick={() => input.current?.click()}>
                {busy ? 'Uploading…' : m.picture_file_id ? 'Replace' : 'Add a picture'}
              </Btn>
            )}
          </span>
        </Field>
      </div>
      {cards.rows.map((c) => (
        <Ln
          key={c.id}
          left={
            <>
              Card {c.card_no} of {cards.rows.length} <small>· {c.code}</small>
            </>
          }
          right={c.raised_line_id ? <Tag kind="un">{where(c)}</Tag> : <small>{where(c)}</small>}
        />
      ))}
      <div className="row mid" style={{ marginTop: 8, gap: 8 }}>
        <Btn primary read onClick={() => void printKanban(m.id, navigate)}>
          Print {names.kanban_card}
        </Btn>
        {canEdit && cards.rows.length > 0 && (
          <Btn small onClick={() => void addKanbanCard(m.id)}>
            Add a card (another bin)
          </Btn>
        )}
        {m.order_quantity === null && <small>No {names.order_quantity} is set, so the card will print it blank.</small>}
      </div>
    </Panel>
  );
}

/**
 * Correcting a count is possible but deliberate: open it, type what was counted and why,
 * look at the change, then confirm. The reason is kept with the movement.
 */
function CountCorrection({ m }: { m: Row }) {
  const s = useSession();
  const [step, setStep] = useState<'closed' | 'typing' | 'confirm'>('closed');
  const [counted, setCounted] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const now = Number(m.stock_on_hand);
  const n = Number(counted);
  const valid = counted.trim() !== '' && !isNaN(n) && n >= 0 && reason.trim().length > 0 && n !== now;
  const close = () => {
    setStep('closed');
    setCounted('');
    setReason('');
  };
  if (step === 'closed') {
    return (
      <div style={{ marginTop: 10 }}>
        <button type="button" className="linklike" onClick={() => setStep('typing')}>
          Correct the count…
        </button>
      </div>
    );
  }
  return (
    <div className="panel soft" style={{ marginTop: 10 }}>
      {step === 'typing' ? (
        <>
          <div className="form">
            <Field label={`Counted on the shelf (${m.unit ?? 'units'})`}>
              <input type="number" min="0" step="any" value={counted} onChange={(e) => setCounted(e.target.value)} autoFocus />
            </Field>
            <Field label="Why it is off">
              <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="recount, damaged, found more…" />
            </Field>
          </div>
          <div style={{ marginTop: 8 }}>
            <Btn small disabled={!valid} onClick={() => setStep('confirm')}>
              Review the change
            </Btn>{' '}
            <button type="button" className="linklike" onClick={close}>
              Cancel
            </button>
            {counted.trim() !== '' && n === now && <small> · that is already the count</small>}
          </div>
        </>
      ) : (
        <>
          <p>
            Change <b>{m.description}</b> from <b>{qty(now)}</b> to <b>{qty(n)}</b> {m.unit ?? ''} ({n > now ? '+' : '−'}
            {qty(Math.abs(n - now))})? Reason: “{reason.trim()}”.
          </p>
          <div style={{ marginTop: 8 }}>
            <Btn
              small
              primary
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await correctCount(m.id, n, reason.trim(), s.user?.id ?? null);
                  toast(`Count corrected to ${qty(n)}. The reason is recorded.`);
                  close();
                } finally {
                  setBusy(false);
                }
              }}
            >
              Yes, change the count
            </Btn>{' '}
            <button type="button" className="linklike" onClick={() => setStep('typing')}>
              Back
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** What moved this material's stock, newest first. */
function Movements({ m }: { m: Row }) {
  const inv = useInventory();
  const moves = useRows('stock_movement', { eq: { material_id: m.id }, order: ['-created_at'] });
  const users = useRows('user');
  const [all, setAll] = useState(false);
  if (moves.rows.length === 0) return null;
  const kinds: Record<string, string> = { receive: 'received', consume: 'used', count: 'count corrected', adjust: 'adjusted' };
  const orderOf = (lineId: string | null) => (lineId ? inv.orders.find((o) => o.lines.some((l) => l.row.id === lineId)) : undefined);
  const shown = all ? moves.rows : moves.rows.slice(0, 6);
  return (
    <Panel label={`Stock movements (${moves.rows.length})`}>
      {shown.map((mv) => {
        const o = orderOf(mv.purchase_order_line_id);
        const j = inv.jobs.find((x) => x.job.id === mv.job_id);
        return (
          <Ln
            key={mv.id}
            left={
              <>
                {kinds[mv.kind] ?? mv.kind} {o && `· ${o.po.number ?? 'free receipt'}`} {j && `· ${j.job.scope_name}`}
                <br />
                <small>
                  {longDate(localDay(mv.created_at))} · {users.rows.find((u) => u.id === mv.by_user_id)?.name ?? 'someone'}
                  {mv.reason && mv.reason !== 'received' ? ` · ${mv.reason}` : ''}
                </small>
              </>
            }
            right={
              <span className="num">
                {Number(mv.qty) > 0 ? '+' : '−'}
                {qty(Math.abs(Number(mv.qty)))}
              </span>
            }
          />
        );
      })}
      {moves.rows.length > shown.length && (
        <button type="button" className="linklike read" onClick={() => setAll(true)}>
          Show all {moves.rows.length}
        </button>
      )}
    </Panel>
  );
}

/** What is on its way: the open order lines for this material. */
function OnOrder({ materialId, unit }: { materialId: string; unit: string | null }) {
  const inv = useInventory();
  const stateNames = useOrderStateNames();
  const open = inv.orders.flatMap((o) => o.lines.filter((l) => l.row.material_id === materialId && o.state !== 'closed' && (o.state === 'draft' || Number(l.outstanding) > 0)).map((l) => ({ o, l })));
  if (open.length === 0) return <small>Nothing on order.</small>;
  return (
    <div style={{ marginTop: 8 }}>
      <small>
        On order: <b>{qty(inv.onOrder[materialId] ?? 0)}</b> {unit ?? ''}
      </small>
      {open.map(({ o, l }) => (
        <div key={l.row.id}>
          <small>
            {o.po.number} · {o.vendor?.name ?? ''} · {qty(o.state === 'draft' ? l.row.qty : l.outstanding)} {o.state === 'draft' ? `(${stateNames.draft.toLowerCase()}, not placed)` : l.expected_on ? `expected ${shortDate(l.expected_on)}` : 'lead time not known'}
          </small>
        </div>
      ))}
    </div>
  );
}

/** The estimating price beside what the material has really been costing. */
function Prices({ m, canEdit, save }: { m: Row; canEdit: boolean; save: (patch: Row) => Promise<unknown> }) {
  const inv = useInventory();
  const vendors = useVendors();
  const p = inv.prices.get(m.id);
  const history = inv.history.filter((h) => h.material_id === m.id).reverse();
  const dollars = (v: string | number | null | undefined) => (v === null || v === undefined ? '—' : `$${Number(v).toFixed(Number(v) * 100 === Math.round(Number(v) * 100) ? 2 : 4)}`);
  return (
    <Panel
      label="Prices"
      right={
        <Info left>
          The <b>estimating price</b> is the one every estimate uses. It never changes on its own. <b>Last paid</b> and the <b>average</b> come from the actual prices on purchase orders. When the last price paid is not the estimating price the material is flagged,
          and you decide whether to change it: a one-time sale price should not become the price going forward.
        </Info>
      }
    >
      <div className="form">
        <Field label="Estimating price">
          <Edit disabled={!canEdit} value={m.price === null ? '' : Number(m.price).toFixed(2)} parse={number} onSave={(v) => save({ price: v === '' ? null : Number(v) })} />
        </Field>
        <Field label="Last paid">
          <input type="text" disabled value={p?.last_paid ? `${dollars(p.last_paid)} · ${shortDate(p.last_paid_at)}${p.last_vendor_id ? ` · ${vendors.name(p.last_vendor_id) ?? ''}` : ''}` : 'never bought through an order'} />
        </Field>
        <Field label={p ? `Average, last ${p.window_months} months` : 'Average'}>
          <input type="text" disabled value={p?.average ? `${dollars(p.average)} · ${p.average_count} ${p.average_count === 1 ? 'purchase' : 'purchases'}, weighted by quantity` : p ? 'nothing bought in that time' : '—'} />
        </Field>
      </div>
      {p?.differs && (
        <div className="warn" style={{ marginTop: 8 }}>
          Last paid {dollars(p.last_paid)} is not the estimating price{m.price === null ? ' (none is set)' : ` ${dollars(m.price)}`}.{' '}
          {canEdit && (
            <Btn small onClick={() => void save({ price: Number(p.last_paid) })}>
              Use {dollars(p.last_paid)} for estimates
            </Btn>
          )}
        </div>
      )}
      {history.length > 0 && (
        <div className="tblwrap" style={{ marginTop: 8 }}>
          <table>
            <thead>
              <tr>
                <th>Bought</th>
                <th>Vendor</th>
                <th>Order</th>
                <th className="right">Qty</th>
                <th className="right">Price paid</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.purchase_order_line_id}>
                  <td>{longDate(localDay(h.bought_at))}</td>
                  <td>{vendors.name(h.vendor_id) ?? '—'}</td>
                  <td>{h.number ?? 'receipt'}</td>
                  <td className="right num">
                    {qty(h.qty)} <small>{m.unit ?? ''}</small>
                  </td>
                  <td className="right num">{dollars(h.unit_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
